import { useRef, useState } from "react";
import { Spinner, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  FiArrowRight,
  FiChevronLeft,
  FiInfo,
  FiLock,
  FiUser,
} from "react-icons/fi";

import { VBabyColors } from "../../colors";
import TermsLabel from "../../components/TermsLabel";
import {
  BackButton,
  Body,
  Card,
  EmotionPanel,
  FieldError,
  FieldGroup,
  FieldInput,
  FieldLabel,
  IconCircle,
  IconNote,
  PanelSubtitle,
  PanelTitle,
  PrimaryButton,
  Shell,
  TermsLine,
} from "../../components/vbaby";
import { useExam } from "../../contexts/examContext";
import { useSnackBar } from "../../contexts/snackbar";
import { pixBuy } from "../../lib/api";
import { formatPrice } from "../../utils/formatPrice";
import { hasLastName } from "../../utils/hasLastName";
import { cpfMask } from "../../utils/masks";
import { validateCpf } from "../../utils/validateCpf";
import { validateEmail } from "../../utils/validateEmail";
import { isSubmitDisabled } from "./rules";

const VALIDATORS = {
  email: validateEmail,
  name: hasLastName,
  cpf: validateCpf,
};

export const PixScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const [exam] = useExam();
  const { form, setForm } = useOutletContext();

  const [errors, setErrors] = useState({ email: false, name: false, cpf: false });
  const [isSendingPayment, setIsSendingPayment] = useState(false);
  const [isSubmissionBlocked, setIsSubmissionBlocked] = useState(false);
  const isSubmittingRef = useRef(false);

  const price = exam?.payment?.price;

  const handleSubmitPurchase = async () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSendingPayment(true);

    let order = null;

    try {
      const response = await pixBuy({
        data: {
          cpf: form.cpf.replace(/\D/g, ""),
          email: form.email,
          name: form.name,
        },
      });

      order = response?.data?.order ?? null;
    } catch {
      showSnackBar(t("payment.errorToPay"), "error");
      isSubmittingRef.current = false;
      setIsSendingPayment(false);

      return;
    }

    if (!order) {
      showSnackBar(t("payment.errorToPay"), "error");
      setIsSubmissionBlocked(true);
      setIsSendingPayment(false);

      return;
    }

    navigate(`/finish-purchase`, {
      state: {
        id: "",
        payment_method: "pix",
        value: price,
        ...order,
      },
    });
  };

  const handleChange = (field) => (event) => {
    const value =
      field === "cpf" ? cpfMask(event.target.value) : event.target.value;

    setErrors((prev) => (prev[field] ? { ...prev, [field]: false } : prev));
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field) => (event) => {
    const value = event.target.value;

    setErrors((prev) => ({
      ...prev,
      [field]: value !== "" && !VALIDATORS[field](value),
    }));
  };

  const isDisabled = isSubmitDisabled({ method: "pix", form });
  const payLabel = t("payment.generatePixCode");
  const subtitle = [
    t("payment.summaryTitle"),
    Number.isFinite(price) ? formatPrice(price) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Shell size="narrow">
      <BackButton type="button" onClick={() => navigate("/checkout")}>
        <FiChevronLeft aria-hidden="true" />
        {t("buttons.back")}
      </BackButton>

      <Card>
        <EmotionPanel align="start" density="header">
          <PanelTitle size="header">{t("payment.payWithPix")}</PanelTitle>
          <PanelSubtitle fontSize="12.5px">{subtitle}</PanelSubtitle>
        </EmotionPanel>

        <Body>
          <FieldGroup>
            <FieldLabel htmlFor="pix-email">E-mail</FieldLabel>
            <FieldInput
              id="pix-email"
              type="email"
              aria-invalid={errors.email}
              value={form.email}
              onChange={handleChange("email")}
              onBlur={handleBlur("email")}
            />
            {errors.email && (
              <FieldError role="alert">{t("payment.invalidEmail")}</FieldError>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="pix-name">{t("payment.name")}</FieldLabel>
            <FieldInput
              id="pix-name"
              aria-invalid={errors.name}
              value={form.name}
              onChange={handleChange("name")}
              onBlur={handleBlur("name")}
            />
            {errors.name && (
              <FieldError role="alert">{t("payment.invalidName")}</FieldError>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="pix-cpf">CPF</FieldLabel>
            <FieldInput
              id="pix-cpf"
              inputMode="numeric"
              maxLength={14}
              aria-invalid={errors.cpf}
              aria-describedby="pix-cpf-reason"
              value={cpfMask(form.cpf)}
              onChange={handleChange("cpf")}
              onBlur={handleBlur("cpf")}
            />
            {errors.cpf && (
              <FieldError role="alert">{t("payment.invalidCpf")}</FieldError>
            )}
          </FieldGroup>

          <IconNote id="pix-cpf-reason" fontSize="11.5px" fontWeight="400">
            <FiInfo aria-hidden="true" />
            {t("payment.cpfReason")}
          </IconNote>

          <PrimaryButton
            type="button"
            disabled={isDisabled || isSendingPayment || isSubmissionBlocked}
            aria-busy={isSendingPayment}
            aria-label={isSendingPayment ? payLabel : undefined}
            onClick={handleSubmitPurchase}
          >
            {isSendingPayment ? (
              <Spinner size="sm" borderWidth="2px" />
            ) : (
              <>
                {payLabel}
                <FiArrowRight aria-hidden="true" />
              </>
            )}
          </PrimaryButton>

          <IconNote justifyContent="center">
            <FiLock aria-hidden="true" />
            {t("payment.dataProtected")}
          </IconNote>

          <TermsLine>
            <TermsLabel
              label={t("payment.termsAgreement")}
              linkColor={VBabyColors.primaryDark}
            />
          </TermsLine>
        </Body>
      </Card>
    </Shell>
  );
};
